import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ServiceCategory } from '@prisma/client';
import { getAvailableTimes } from '../../services/availability';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';
import { TakenSessionSlot } from '../../types/appointments';

function useDayAvailableTimes({
  day,
  employeeId,
  timezone,
  duration,
  rescheduleAppointmentId,
  serviceCategory,
  takenSlots,
}: {
  day: Date | null;
  employeeId: string;
  timezone: string;
  duration: number | string;
  rescheduleAppointmentId?: string;
  serviceCategory?: ServiceCategory;
  takenSlots?: TakenSessionSlot[];
}) {
  const { user } = useUser<LoadedUser>();
  const formattedDay = day ? format(day, 'yyyy-MM-dd') : null;

  const { data, isPlaceholderData, isLoading, isFetching, isError, error } =
    useQuery({
      queryKey: [
        'dayAvailableTimes',
        formattedDay,
        user?.business?.id,
        duration,
        employeeId,
        timezone,
        rescheduleAppointmentId,
        serviceCategory,
        takenSlots,
      ],
      queryFn: async () => {
        if (!formattedDay || !user?.business?.id) return null;

        const { availableTimes } = await getAvailableTimes({
          startDate: formattedDay,
          endDate: formattedDay,
          duration: parseInt(String(duration), 10),
          employeeId,
          timezone,
          businessId: user?.business?.id,
          rescheduleAppointmentId,
          serviceCategory,
          takenSlots,
        });

        return availableTimes;
      },
      enabled: !!user,
      staleTime: 1000 * 30, // 30 seconds
      gcTime: 1000 * 60, // 60 seconds
    });

  return {
    data: isPlaceholderData ? null : data,
    isPlaceholderData,
    isLoading,
    isFetching,
    isError,
    error,
  };
}

export default useDayAvailableTimes;
